import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiErrorFromResponse, authFetch } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'

/**
 * Thumbnail tools — staying on the legacy multipart/JSON endpoints until
 * the ``tool_jobs`` refactor lands (tracked in
 * https://github.com/LucasCalazans/prism/issues/5). The ``thumb_path``
 * token returned by /preview is held opaquely in the calling component's
 * state and threaded into /apply — not exposed to the user.
 */

export interface GenerateThumbnailInput {
  channel: string
  /** ``<lang>/<video_id>`` — the system project to derive the background frame from. */
  systemVideo: string
  text: string
  /** Hex color, e.g. ``#FFD700``. */
  highlightColor: string
  /** Thumbnail style key (matches ``_STYLE_TEMPLATES`` on the backend). Empty
   * lets the channel/global default win. */
  style?: string
}

export interface GenerateThumbnailOut {
  job_id: string
  download_url: string
  thumb_path: string
  system_video: string | null
  source: string
  duration_s: number
}

export interface SuggestTextInput {
  channel: string
  systemVideo: string
}

export interface SuggestTextOut {
  text: string
  source: 'existing' | 'generated' | 'empty'
  error?: string
}

export interface ApplyThumbnailInput {
  channel: string
  systemVideo: string
  thumbPath: string
}

export interface ApplyThumbnailOut {
  thumbnail_path: string
}

/**
 * Generate a preview thumbnail (PNG staged under output/<ch>/.tools/<job_id>/).
 * Multipart because the legacy endpoint accepts uploaded video/image files
 * (we don't use those here — only ``system_video`` — but the route still
 * expects ``application/x-www-form-urlencoded``-ish form data).
 */
export function useGenerateThumbnail() {
  return useMutation({
    mutationFn: async (input: GenerateThumbnailInput): Promise<GenerateThumbnailOut> => {
      const form = new FormData()
      form.append('channel', input.channel)
      form.append('system_video', input.systemVideo)
      form.append('text', input.text)
      form.append('highlight_color', input.highlightColor)
      if (input.style) form.append('style', input.style)

      const res = await authFetch('/api/v1/tools/thumbnail', {
        method: 'POST',
        body: form,
        credentials: 'same-origin',
      })
      if (!res.ok) {
        throw await apiErrorFromResponse(res, 'Falha ao gerar thumbnail')
      }
      const body = (await res.json()) as { data: GenerateThumbnailOut }
      return body.data
    },
  })
}

/**
 * Asks the backend for thumbnail copy: returns the existing
 * ``metadata.thumbnail_text`` if present, otherwise generates via the configured LLM
 * from the script. Empty when nothing's available.
 */
export function useSuggestThumbnailText() {
  return useMutation({
    mutationFn: async (input: SuggestTextInput): Promise<SuggestTextOut> => {
      const form = new FormData()
      form.append('channel', input.channel)
      form.append('system_video', input.systemVideo)

      const res = await authFetch('/api/v1/tools/thumbnail-suggest-text', {
        method: 'POST',
        body: form,
        credentials: 'same-origin',
      })
      if (!res.ok) {
        throw await apiErrorFromResponse(res, 'Falha ao sugerir texto')
      }
      const body = (await res.json()) as { data: SuggestTextOut }
      return body.data
    },
  })
}

/**
 * Promotes a generated preview to be the project's official thumbnail.
 * After success the video query is invalidated so the player + project
 * card pick up the new thumbnail without a page reload.
 */
export function useApplyThumbnail() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: ApplyThumbnailInput): Promise<ApplyThumbnailOut> => {
      const res = await authFetch('/api/v1/tools/thumbnail-apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          channel: input.channel,
          system_video: input.systemVideo,
          thumb_path: input.thumbPath,
        }),
        credentials: 'same-origin',
      })
      if (!res.ok) {
        throw await apiErrorFromResponse(res, 'Falha ao aplicar thumbnail')
      }
      const body = (await res.json()) as { data: ApplyThumbnailOut }
      return body.data
    },
    onSuccess: (_data, vars) => {
      const [lang, vid] = vars.systemVideo.split('/')
      if (vid && lang) {
        void qc.invalidateQueries({ queryKey: queryKeys.videos.byId(vid, lang) })
      }
      // Bust thumbnail image caches by invalidating the cross-cutting key.
      void qc.invalidateQueries({ queryKey: queryKeys.thumbnail.all })
    },
  })
}
